import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import Header from "./Header";
import Sidebar from "./Sidebar";
import ConfirmModal from "./ConfirmModal";
import StatusResultModal from "./StatusResultModal";
import "../styles/UserManagement.css";
import { useSidebar } from "../App";
import { getApiUrl, getCsrfToken } from "../utils/apiHelper";

export default function UserManagement() {
    const { t, i18n } = useTranslation("userManagement");
    const { sidebarCollapsed } = useSidebar();
    const [formData, setFormData] = useState({
        firstName: "",
        lastName: "",
        email: "",
    });

    const [users, setUsers] = useState([]);
    const [apiResponse, setApiResponse] = useState(null);
    const [loading, setLoading] = useState(false);
    const [fetchLoading, setFetchLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [pendingDeleteId, setPendingDeleteId] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [statusModal, setStatusModal] = useState({
        open: false,
        type: "success",
        message: "",
    });

    const locale = (i18n.resolvedLanguage || i18n.language || "en").split("-")[0];

    const formatDate = (dateString) => {
        if (!dateString) return t("notAvailable");
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString(locale) + ' ' + date.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
        } catch (error) {
            console.error("Error formatting date:", error);
            return t("invalidDate");
        }
    };

    // Fetch users on component mount
    useEffect(() => {
        fetchUsers();
    }, []);

    const fetchUsers = async () => {
        setFetchLoading(true);
        setError(null);
        
        try {
            const response = await fetch(getApiUrl('/api/internal/employees/all/'), {
                method: "GET",
                credentials: 'include', // Send session cookies
                redirect: "follow"
            });
            
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.detail || errorData.message || `HTTP ${response.status}`);
            }

            const result = await response.json();
            console.log('Fetched users:', result);

            // Map the API response to match our component structure
            if (result && result.employees && Array.isArray(result.employees)) {
                const mappedUsers = result.employees.map(user => ({
                    userId: user.id || '',
                    firstName: user.fname || '',
                    lastName: user.lname || '',
                    email: user.email || '',
                    role: user.role || '',
                    group: user.role || '', // Using role as group for now
                    contact: user.contact || '',
                    machineId: user.machine_id || '',
                    lastLogin: user.last_login || null,
                    accountCreated: user.account_created || null
                }));
                setUsers(mappedUsers);
            }
        } catch (err) {
            console.error('Error fetching users:', err);
            setError(err.message);
        } finally {
            setFetchLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const handleDelete = async () => {
        const userId = pendingDeleteId;
        setPendingDeleteId(null);
        if (!userId) return;

        try {
            // Get CSRF token for session-based authentication
            const csrfToken = await getCsrfToken();

            const response = await fetch(getApiUrl(`/api/internal/employees/${userId}/delete/`), {
                method: "POST",
                credentials: 'include', // Send session cookies
                headers: {
                    'X-CSRFToken': csrfToken
                },
                redirect: "follow"
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.detail || errorData.message || `HTTP ${response.status}`);
            }

            // Refresh the user list after successful deletion
            await fetchUsers();
            setStatusModal({
                open: true,
                type: "success",
                message: t("deleteSuccess"),
            });
        } catch (err) {
            console.error('Error deleting user:', err);
            setStatusModal({
                open: true,
                type: "error",
                message: t("deleteFailed", { message: err.message }),
            });
        }
    };

    const handleAdd = async () => {
        // Validate required fields
        if (!formData.firstName?.trim() || !formData.lastName?.trim() || !formData.email?.trim()) {
            setError(t("allFieldsRequired"));
            return;
        }

        setLoading(true);
        setError(null);
        try {
            // Get CSRF token for session-based authentication
            const csrfToken = await getCsrfToken();

            const formdata = new FormData();
            // Removed: admin_id is now obtained from session
            formdata.append("fname", formData.firstName.trim());
            formdata.append("lname", formData.lastName.trim());
            formdata.append("email", formData.email.trim());
            formdata.append("address", "n/a");

            const response = await fetch(getApiUrl("/api/internal/employees/"), {
                method: "POST",
                credentials: 'include', // Send session cookies
                headers: {
                    'X-CSRFToken': csrfToken
                },
                body: formdata,
                redirect: "follow"
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.detail || errorData.message || `HTTP ${response.status}`);
            }

            const data = await response.json();
            setApiResponse(data);

            // Refresh the user list after successful add
            await fetchUsers();

            // Reset form and close modal
            setFormData({
                firstName: "",
                lastName: "",
                email: "",
            });
            setShowModal(false);
            setStatusModal({
                open: true,
                type: "success",
                message: t("addSuccess"),
            });
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const filteredUsers = users.filter((user) => {
        if (!searchQuery) return true;
        const query = searchQuery.toLowerCase();
        const fullName = `${user.firstName} ${user.lastName}`.toLowerCase();
        return (
            fullName.includes(query) ||
            (user.email || '').toLowerCase().includes(query) ||
            (user.userId || '').toLowerCase().includes(query)
        );
    });

    return (
        <div className="theme-whbl">
            <Header />

            <ConfirmModal
                open={pendingDeleteId != null}
                title={t("deleteUser")}
                message={t("deleteConfirm")}
                confirmLabel={t("deleteUser")}
                cancelLabel={t("modal.cancel")}
                onCancel={() => setPendingDeleteId(null)}
                onConfirm={handleDelete}
            />

            <StatusResultModal
                open={statusModal.open}
                type={statusModal.type}
                message={statusModal.message}
                onClose={() =>
                    setStatusModal({ open: false, type: "success", message: "" })
                }
            />

            <div className="container-fluid">
                <div className="main-container">
                    {/* Sidebar column */}
                    <div className={`${sidebarCollapsed ? 'col-1' : 'col-2'} p-0`}>
                        <Sidebar />
                    </div>

                    {/* Main content column */}
                    <main
                        className={`${sidebarCollapsed ? 'col-11' : 'col-10'}`}
                        style={{ width: "100%", overflowX: "auto" }}
                    >
                        <div className="um-container">
                            <div className="um-header">
                                <div className="um-title-section">
                                    <h1 className="um-title">{t("title")}</h1>
                                </div>
                                <button className="um-create-btn" onClick={() => setShowModal(true)}>
                                    {t("addEmployee")}
                                </button>
                            </div>

                            <div className="um-search-container">
                                <input
                                    type="text"
                                    className="um-search-input"
                                    placeholder={t("searchPlaceholder")}
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                />
                            </div>

                            <div className="um-user-table">
                                <div className="um-user-table-header">
                                    <div className="um-user-table-cell um-user-table-cell-name">{t("table.employeeName")}</div>
                                    <div className="um-user-table-cell um-user-table-cell-email">{t("table.email")}</div>
                                    <div className="um-user-table-cell um-user-table-cell-last-login">{t("table.lastLogin")}</div>
                                    <div className="um-user-table-cell um-user-table-cell-actions">{t("table.actions")}</div>
                                </div>
                                <div className="um-user-table-body">
                                    {fetchLoading ? (
                                        <div className="um-empty-state">
                                            <i className="fas fa-spinner fa-spin" style={{ marginRight: '8px' }}></i>
                                            {t("loading")}
                                        </div>
                                    ) : filteredUsers.length === 0 ? (
                                        <div className="um-empty-state">
                                            {searchQuery ? t("emptySearch") : t("empty")}
                                        </div>
                                    ) : (
                                        filteredUsers.map((user) => (
                                            <div key={user.userId} className="um-user-table-row">
                                                <div className="um-user-table-cell um-user-table-cell-name">
                                                    <div className="um-user-name">{`${user.firstName} ${user.lastName}`}</div>
                                                </div>
                                                <div className="um-user-table-cell um-user-table-cell-email">
                                                    <div className="um-user-email">{user.email || t("notAvailable")}</div>
                                                </div>
                                                <div className="um-user-table-cell um-user-table-cell-last-login">
                                                    <div className="um-user-last-login">
                                                        {user.lastLogin ? formatDate(user.lastLogin) : t("notAvailable")}
                                                    </div>
                                                </div>
                                                <div className="um-user-table-cell um-user-table-cell-actions">
                                                    <button
                                                        className="um-action-btn um-delete-btn"
                                                        onClick={() => setPendingDeleteId(user.userId)}
                                                        title={t("deleteUser")}
                                                    >
                                                        <i className="fas fa-trash"></i>
                                                    </button>
                                                </div>
                                            </div>
                                        ))
                                    )}
                                </div>
                            </div>

                            {/* Add User Modal */}
                            {showModal && (
                                <>
                                    <div className="um-modal-overlay" onClick={() => setShowModal(false)}></div>
                                    <div className="um-modal">
                                        <div className="um-modal-header">
                                            <h2 className="um-modal-title">{t("modal.title")}</h2>
                                            <button
                                                className="um-modal-close"
                                                onClick={() => setShowModal(false)}
                                            >
                                                <i className="fas fa-times"></i>
                                            </button>
                                        </div>
                                        <div className="um-modal-body">
                                            <form
                                                onSubmit={(e) => {
                                                    e.preventDefault();
                                                    handleAdd();
                                                }}
                                            >
                                                <div className="um-form-grid">
                                                    <div className="um-form-group">
                                                        <label className="um-label">{t("modal.firstName")}</label>
                                                        <input
                                                            type="text"
                                                            className="um-input"
                                                            name="firstName"
                                                            placeholder={t("modal.firstNamePlaceholder")}
                                                            value={formData.firstName}
                                                            onChange={handleChange}
                                                            required
                                                        />
                                                    </div>
                                                    <div className="um-form-group">
                                                        <label className="um-label">{t("modal.lastName")}</label>
                                                        <input
                                                            type="text"
                                                            className="um-input"
                                                            name="lastName"
                                                            placeholder={t("modal.lastNamePlaceholder")}
                                                            value={formData.lastName}
                                                            onChange={handleChange}
                                                            required
                                                        />
                                                    </div>
                                                    <div className="um-form-group" style={{ gridColumn: '1 / -1' }}>
                                                        <label className="um-label">{t("modal.email")}</label>
                                                        <input
                                                            type="email"
                                                            className="um-input"
                                                            name="email"
                                                            placeholder={t("modal.emailPlaceholder")}
                                                            value={formData.email}
                                                            onChange={handleChange}
                                                            required
                                                        />
                                                    </div>
                                                </div>

                                                {error && (
                                                    <div className="um-error-message">
                                                        <i className="fas fa-exclamation-circle"></i>
                                                        {error}
                                                    </div>
                                                )}

                                                <div className="um-modal-actions">
                                                    <button
                                                        type="button"
                                                        className="um-cancel-btn"
                                                        onClick={() => setShowModal(false)}
                                                    >
                                                        {t("modal.cancel")}
                                                    </button>
                                                    <button
                                                        type="submit"
                                                        className="um-submit-btn"
                                                        disabled={loading}
                                                    >
                                                        {loading ? t("modal.adding") : t("modal.addUser")}
                                                    </button>
                                                </div>
                                            </form>
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    </main>
                </div>
            </div>
        </div>
    );
}
